/**
 * guidesData.ts — content for /guides/:slug how-to pages.
 *
 * Targets high-intent queries:
 *   "how to connect OAuth MCPs"
 *   "Claude Desktop MCP setup"
 *   "OpenAPI to MCP converter"
 *   "API to MCP guide"
 */

export type GuideStep = {
  title: string;
  body: string;
  /** Optional code/config block to display verbatim */
  code?: string;
  codeLabel?: string;
};

export type GuideEntry = {
  slug: string;
  pageTitle: string;
  metaDescription: string;
  headline: string;
  subheadline: string;
  /** Time estimate shown in hero, e.g. "5 minutes" */
  timeEstimate: string;
  /** Short intro paragraph */
  intro: string;
  steps: GuideStep[];
  /** Key terms + definitions shown in a "What is X?" box */
  glossary?: { term: string; definition: string }[];
  faq: { q: string; a: string }[];
  /** CTAs at page bottom */
  primaryCtaLabel: string;
  primaryCtaHref: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  /** Related guide slugs */
  related: string[];
};

// ─────────────────────────────────────────────────────────────────────────────

const openApiToMcp: GuideEntry = {
  slug: "openapi-to-mcp",
  pageTitle: "OpenAPI to MCP Converter — Turn Any API Spec into an MCP Server",
  metaDescription:
    "Learn how to convert an OpenAPI or Swagger spec into a live MCP server in minutes. Step-by-step guide — no code required.",
  headline: "Convert OpenAPI to MCP",
  subheadline: "Turn any OpenAPI or Swagger spec into a live MCP server — in minutes, with no code.",
  timeEstimate: "5 minutes",
  intro:
    "The Model Context Protocol (MCP) is how AI agents like Claude talk to external services. If your API has an OpenAPI (formerly Swagger) spec, you can convert it to a live MCP server without writing a single line of code. This guide walks you through the exact steps.",
  steps: [
    {
      title: "Get your OpenAPI spec",
      body: "You need an OpenAPI 3.x or Swagger 2.x spec in JSON or YAML format. Most APIs publish this at a URL like /openapi.json or /docs. If yours doesn't, you can paste the spec directly or upload a file. Swagger 2 specs are automatically upgraded to OpenAPI 3 during conversion.",
    },
    {
      title: "Paste, upload, or provide the spec URL",
      body: "On the converter homepage, choose your input method: paste the raw JSON/YAML, upload a .json or .yaml file, or enter the URL where the spec lives (e.g. https://petstore3.swagger.io/api/v3/openapi.json). The converter fetches and parses the spec automatically.",
    },
    {
      title: "Configure authentication (if needed)",
      body: "The converter detects whether your API uses API key, Bearer token, Basic auth, or OAuth. If auth is required, you'll be prompted to provide a credential so the MCP can proxy authenticated requests. Public APIs can skip this step entirely.",
    },
    {
      title: "Preview the tools",
      body: "Before hosting, you can preview exactly what tools (endpoints) your MCP will expose. Each OpenAPI operation becomes a callable MCP tool with typed parameters. Review the list and verify the tools look correct.",
    },
    {
      title: "Host your MCP",
      body: "Click Host. Within a few seconds your MCP server is live at a permanent HTTPS endpoint. Free accounts get 3 conversions per month with a 7-day expiry. Pro accounts get unlimited conversions that never expire.",
    },
    {
      title: "Connect to Claude Desktop",
      body: "Copy the generated endpoint URL and add it to your Claude Desktop config file. No additional setup needed — Claude immediately sees all your API's tools.",
      code: `{
  "mcpServers": {
    "my-api": {
      "url": "https://api-to-mcp.onrender.com/mcp/YOUR_MCP_ID"
    }
  }
}`,
      codeLabel: "claude_desktop_config.json",
    },
  ],
  glossary: [
    { term: "OpenAPI", definition: "A standard specification format (formerly Swagger) for describing REST APIs — their endpoints, parameters, authentication, and response shapes." },
    { term: "MCP (Model Context Protocol)", definition: "An open protocol by Anthropic that lets AI models like Claude call external tools and services in a structured, safe way." },
    { term: "MCP Server", definition: "A hosted service that exposes a set of tools over the MCP protocol. Claude connects to it to call your API." },
    { term: "Tool", definition: "In MCP terms, a callable function — one per API endpoint. Claude can discover and invoke these tools in conversation." },
  ],
  faq: [
    { q: "What OpenAPI versions are supported?", a: "OpenAPI 3.0 and 3.1 are fully supported. Swagger 2.0 specs are automatically upgraded during conversion." },
    { q: "Does my API need to be public?", a: "No. You can convert any API — internal, private, or public. For private APIs, you provide your credentials when converting and they're stored securely." },
    { q: "How long does conversion take?", a: "Usually under 5 seconds. Large specs with 100+ endpoints may take a few seconds longer." },
    { q: "Can Claude call my API directly without this tool?", a: "Claude can browse the web but cannot call arbitrary APIs directly. MCP gives it a structured, secure channel to do exactly that — with typed parameters and proper error handling." },
    { q: "What happens if my spec changes?", a: "Re-convert with the updated spec. If you're on Pro, you can version your MCPs and roll back if needed." },
    { q: "Is there a size limit on specs?", a: "Specs up to 5 MB are supported. Most real-world API specs are well under 1 MB." },
  ],
  primaryCtaLabel: "Convert your OpenAPI spec now",
  primaryCtaHref: "/",
  secondaryCtaLabel: "Browse example MCPs",
  secondaryCtaHref: "/registry",
  related: ["claude-desktop-mcp-setup", "oauth-mcp-setup"],
};

// ─────────────────────────────────────────────────────────────────────────────

const claudeDesktopSetup: GuideEntry = {
  slug: "claude-desktop-mcp-setup",
  pageTitle: "Claude Desktop MCP Setup — Connect MCP Servers Step by Step",
  metaDescription:
    "Step-by-step guide to connecting MCP servers to Claude Desktop. Covers config file location, JSON format, auth headers, and troubleshooting.",
  headline: "Claude Desktop MCP Setup",
  subheadline: "Connect any MCP server to Claude Desktop in 3 minutes — config file, auth headers, and troubleshooting covered.",
  timeEstimate: "3 minutes",
  intro:
    "Claude Desktop supports MCP servers out of the box. Once connected, Claude can call your tools directly in conversation — no copy-paste, no manual API calls. This guide shows you exactly where the config file lives, the correct JSON format, and how to handle authentication.",
  steps: [
    {
      title: "Find your config file",
      body: "Claude Desktop stores MCP server config in a JSON file on your machine. The location depends on your OS:",
      code: `macOS:  ~/Library/Application Support/Claude/claude_desktop_config.json
Windows: %APPDATA%\\Claude\\claude_desktop_config.json
Linux:   ~/.config/Claude/claude_desktop_config.json`,
      codeLabel: "Config file locations",
    },
    {
      title: "Open and edit the file",
      body: "Open the file in any text editor (VS Code works great). If it doesn't exist, create it. The file should contain a JSON object with a top-level \"mcpServers\" key.",
      code: `{
  "mcpServers": {}
}`,
      codeLabel: "Starting template",
    },
    {
      title: "Add your MCP server",
      body: "Each entry under \"mcpServers\" is a named server. For remote HTTP servers (like the ones on this platform), you only need a URL and optionally headers for authentication.",
      code: `{
  "mcpServers": {
    "my-api": {
      "url": "https://api-to-mcp.onrender.com/mcp/YOUR_MCP_ID"
    }
  }
}`,
      codeLabel: "Remote MCP server (no auth)",
    },
    {
      title: "Add authentication headers (if needed)",
      body: "If your MCP requires an API key or token, pass it as an HTTP header. The exact header name and format depends on the API — check the MCP's setup instructions.",
      code: `{
  "mcpServers": {
    "notion": {
      "url": "https://api-to-mcp.onrender.com/mcp/notion-mcp",
      "headers": {
        "Authorization": "Bearer secret_YOUR_NOTION_TOKEN"
      }
    },
    "linear": {
      "url": "https://api-to-mcp.onrender.com/mcp/linear-mcp",
      "headers": {
        "Authorization": "Bearer lin_api_YOUR_KEY"
      }
    }
  }
}`,
      codeLabel: "Multiple servers with auth",
    },
    {
      title: "Restart Claude Desktop",
      body: "Save the file and fully restart Claude Desktop (quit and reopen — don't just close the window). On restart, Claude loads all configured MCP servers and verifies they're reachable.",
    },
    {
      title: "Verify the connection",
      body: "Open a new conversation in Claude Desktop. In the bottom-left of the text input, you should see a tools icon showing the number of connected MCP tools. Click it to see the full list. Then ask Claude: \"What tools do you have available?\" — it should list everything from your MCPs.",
    },
  ],
  faq: [
    { q: "Claude Desktop doesn't show the tools icon — what's wrong?", a: "Usually a JSON syntax error in the config file. Validate your JSON at jsonlint.com and make sure there are no trailing commas. Also confirm Claude Desktop is fully restarted (check Activity Monitor / Task Manager)." },
    { q: "Can I add multiple MCP servers?", a: "Yes. Add as many named entries as you want under \"mcpServers\". Each gets its own name, URL, and optional headers." },
    { q: "What's the difference between remote and local MCP servers?", a: "Remote servers (like this platform) run over HTTPS — you just provide a URL. Local servers run as a process on your machine and use a \"command\" key instead of a \"url\" key." },
    { q: "Does this work with Cursor or Cline too?", a: "Yes. Cursor and Cline use the same MCP protocol and a similar JSON config format. The server URLs are identical — only the config file location differs." },
    { q: "My token changed — do I need to restart?", a: "Yes. Claude Desktop reads the config on startup. Update the file and restart after any credential change." },
  ],
  primaryCtaLabel: "Find an MCP to connect",
  primaryCtaHref: "/registry",
  secondaryCtaLabel: "Convert your own API",
  secondaryCtaHref: "/",
  related: ["openapi-to-mcp", "oauth-mcp-setup"],
};

// ─────────────────────────────────────────────────────────────────────────────

const oauthMcpSetup: GuideEntry = {
  slug: "oauth-mcp-setup",
  pageTitle: "OAuth MCP Setup — Connect OAuth APIs to Claude Desktop",
  metaDescription:
    "How to connect OAuth-authenticated APIs to Claude via MCP. Covers token handling, header setup, and common OAuth patterns for Claude Desktop.",
  headline: "OAuth MCP Setup",
  subheadline: "Connect OAuth-protected APIs to Claude Desktop — token handling, header setup, and the patterns that actually work.",
  timeEstimate: "10 minutes",
  intro:
    "OAuth is how most modern APIs — GitHub, Google, Notion, Linear, Slack — handle authentication. Connecting an OAuth API to Claude via MCP requires generating an access token and passing it in the right header. This guide explains the pattern clearly and covers the most common OAuth APIs.",
  steps: [
    {
      title: "Understand the two OAuth patterns",
      body: "Most OAuth APIs for developer use give you two options: (1) a long-lived Personal Access Token (PAT) — simple, works like an API key, best for personal MCP use; or (2) a full OAuth 2.0 flow with refresh tokens — more complex, better for multi-user apps. For MCP with Claude Desktop, a PAT is almost always the right choice.",
    },
    {
      title: "Generate a Personal Access Token",
      body: "Each API has its own PAT generation page. Here are the most common ones:",
      code: `GitHub:  Settings → Developer settings → Personal access tokens → Generate new token
Notion:  notion.so/my-integrations → New integration → copy token
Linear:  Settings → API → Personal API keys → Create key
Stripe:  Dashboard → Developers → API keys → Restricted keys
Slack:   api.slack.com/apps → Your App → OAuth & Permissions → Bot Token`,
      codeLabel: "Token generation pages",
    },
    {
      title: "Identify the correct auth header",
      body: "Most APIs use the Authorization header with a Bearer scheme. Some use a custom header. Check the API's docs or the MCP's setup instructions for the exact format.",
      code: `# Most common — Bearer token
Authorization: Bearer YOUR_TOKEN

# Some APIs use a raw header
X-API-Key: YOUR_KEY

# Notion specifically requires "Bearer" prefix
Authorization: Bearer secret_YOUR_NOTION_TOKEN`,
      codeLabel: "Common auth header patterns",
    },
    {
      title: "Add the header to your Claude Desktop config",
      body: "In your claude_desktop_config.json, add the token under the \"headers\" key for the relevant MCP server.",
      code: `{
  "mcpServers": {
    "github": {
      "url": "https://api-to-mcp.onrender.com/mcp/github-mcp",
      "headers": {
        "Authorization": "Bearer ghp_YOUR_GITHUB_TOKEN"
      }
    },
    "notion": {
      "url": "https://api-to-mcp.onrender.com/mcp/notion-mcp",
      "headers": {
        "Authorization": "Bearer secret_YOUR_NOTION_TOKEN"
      }
    }
  }
}`,
      codeLabel: "claude_desktop_config.json",
    },
    {
      title: "Scope your token to minimum permissions",
      body: "Always create tokens with the minimum scopes needed. If the MCP only reads issues, don't grant write access. Most PAT systems let you pick specific permissions — use them. This limits the blast radius if a token is ever exposed.",
    },
    {
      title: "Restart and verify",
      body: "Save the config file and restart Claude Desktop completely. Then ask Claude: \"Can you list my [GitHub repos / Notion pages / Linear issues]?\" — it should respond with live data from your account.",
    },
  ],
  glossary: [
    { term: "OAuth 2.0", definition: "An open authorization framework that lets users grant apps access to their accounts without sharing passwords. Most modern APIs use it." },
    { term: "Personal Access Token (PAT)", definition: "A long-lived token that acts like a username+password for an API. Easier to use than a full OAuth flow for developer tools." },
    { term: "Bearer Token", definition: "An HTTP authentication scheme where you send the token in the Authorization header as 'Bearer TOKEN'. Used by most OAuth APIs." },
    { term: "Scope", definition: "A permission you grant when creating a token — e.g. repo:read lets you read repositories but not write. Use the minimum scope you need." },
  ],
  faq: [
    { q: "Should I store my token in the config file?", a: "The config file is local to your machine and never uploaded by Claude Desktop. For personal use it's fine. Never commit the config file to a public git repo." },
    { q: "My token expired — how do I update it?", a: "Generate a new token in the API's dashboard, update the config file, and restart Claude Desktop." },
    { q: "What about APIs that require a full OAuth flow with redirect URLs?", a: "For those, you need to complete the OAuth flow in a browser to get an access token, then paste the resulting token into the config. The token may expire after hours or days depending on the API." },
    { q: "Can I use the same MCP across multiple Claude instances?", a: "Yes. The MCP server URL is the same — each instance just needs the header in its own config file." },
    { q: "Is it safe to put my Notion/GitHub token in a config file?", a: "Yes for personal machines. The file is local, not synced anywhere by default, and Claude Desktop doesn't transmit it beyond using it for API calls." },
  ],
  primaryCtaLabel: "Browse OAuth MCPs",
  primaryCtaHref: "/registry",
  secondaryCtaLabel: "Claude Desktop setup guide",
  secondaryCtaHref: "/guides/claude-desktop-mcp-setup",
  related: ["claude-desktop-mcp-setup", "openapi-to-mcp"],
};

// ─────────────────────────────────────────────────────────────────────────────

export const GUIDES_DATA: GuideEntry[] = [
  openApiToMcp,
  claudeDesktopSetup,
  oauthMcpSetup,
];

export function getGuideBySlug(slug: string): GuideEntry | undefined {
  return GUIDES_DATA.find((g) => g.slug === slug);
}
